import React, { useMemo } from 'react';
import ReactSelect, { MenuPlacement } from 'react-select';

import SelectInput from './components/select-input/select-input';
import SelectOptionWrapper from './components/select-option-wrapper/select-option-wrapper';
import SelectToggle from './components/select-toggle/select-toggle';
import SelectValueWrapper from './components/select-value-wrapper/select-value-wrapper';
import Input from '../input/input';
import { InputError } from '../input/input-types';
import { InputMode, SelectRef } from './select-types';

import generateUID from '../../shared/utils/generate-uid/generate-uid';

import styles from './select.scss';

interface SelectProps<Option> {
  'aria-label'?: string;
  'aria-labelledby'?: string;
  blurInputOnSelect?: boolean;
  className?: string;
  'data-testid'?: string;
  error?: InputError;
  getOptionValue?: (option: Option) => string;
  id?: string;
  infoMessage?: string;
  inputMode?: InputMode;
  isDisabled?: boolean;
  isMenuOpenByDefault?: boolean;
  isOptionSelected?: (
    option: Option,
    selectValue: readonly Option[]
  ) => boolean;
  isRequired?: boolean;
  isSearchable?: boolean;
  label?: string;
  listboxId?: string;
  menuPlacement?: MenuPlacement;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  onChange?: (option: Option) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
  openMenuOnFocus?: boolean;
  options?: Option[];
  placeholder?: string;
  renderNoResultsFound?: (props: { inputValue: string }) => React.ReactNode;
  renderOption?: (props: Option) => React.ReactElement;
  renderValue?: (props: Option) => React.ReactElement;
  selectRef?: SelectRef<Option>;
  shouldAutofocus?: boolean;
  value?: Option;
}

function Select<Option>(props: SelectProps<Option>) {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    blurInputOnSelect = false,
    className,
    'data-testid': dataTestid,
    getOptionValue,
    id,
    inputMode,
    isDisabled = false,
    isMenuOpenByDefault,
    isOptionSelected,
    isRequired,
    isSearchable = false,
    label = '',
    menuPlacement,
    name,
    onBlur,
    onChange,
    onFocus,
    openMenuOnFocus = false,
    options = [],
    placeholder: placeHolderProp = '',
    renderNoResultsFound: RenderNoResultsFound,
    renderOption: RenderOption,
    renderValue: RenderValue,
    selectRef,
    shouldAutofocus,
    value
  } = props;

  const placeholder = label ? undefined : placeHolderProp;
  const inputId = useMemo(() => id || generateUID(), [id]);
  const components = useMemo(() => {
    const customComponents = {
      DropdownIndicator: SelectToggle
    };

    if (RenderNoResultsFound) {
      customComponents['NoOptionsMessage'] = RenderNoResultsFound;
    }

    if (RenderOption) {
      customComponents['Option'] = props => (
        <SelectOptionWrapper {...props} component={RenderOption} />
      );
    }

    if (RenderValue) {
      customComponents['SingleValue'] = props => (
        <SelectValueWrapper {...props} component={RenderValue} />
      );
    }

    customComponents['Input'] = SelectInput;

    return customComponents;
  }, [RenderNoResultsFound, RenderOption, RenderValue]);

  function handleOnChange(option) {
    onChange?.(option as Option);
  }

  function noOptionsMessage() {
    return 'No results found';
  }

  return (
    <Input
      {...{
        ...props,
        isRequired: !placeholder && isRequired
      }}
      className={`${styles['container']} ${
        menuPlacement === 'top' ? styles['container--menu-top'] : ''
      } ${className}`}
      id={inputId}
      labelClassName={styles['label']}
    >
      <ReactSelect
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        autoFocus={shouldAutofocus}
        blurInputOnSelect={blurInputOnSelect}
        className="react-select"
        classNamePrefix="react-select"
        components={components}
        data-testid={dataTestid}
        defaultMenuIsOpen={isMenuOpenByDefault}
        getOptionValue={getOptionValue}
        inputMode={inputMode}
        isDisabled={isDisabled}
        isOptionSelected={isOptionSelected}
        isSearchable={isSearchable}
        menuPlacement={menuPlacement}
        name={name}
        noOptionsMessage={noOptionsMessage}
        onBlur={onBlur}
        onChange={handleOnChange}
        onFocus={onFocus}
        openMenuOnFocus={openMenuOnFocus}
        options={options}
        placeholder={placeholder || ''}
        ref={selectRef}
        tabSelectsValue={false}
        value={value}
      />
    </Input>
  );
}

export default Select;
